/**
 * Pure data helpers for the path-mode section panel.
 *
 * No Cesium / DOM / Svelte dependencies — safe to import from any context.
 */

import { Color } from 'cesium';
import type {
	ISectionCircuitInfo,
	ISectionInfo,
	ISectionInfoMast
} from '../map-tools-store';

// ── Section colour palette ──────────────────────────────────────────────────

/** Cycled per section index; each section gets a stable colour. */
export const SECTION_COLORS = [
	'#10b981', // emerald-500
	'#0ea5e9', // sky-500
	'#f59e0b', // amber-500
	'#8b5cf6', // violet-500
	'#f43f5e', // rose-500
	'#06b6d4', // cyan-500
	'#d946ef', // fuchsia-500
	'#84cc16' // lime-500
] as const;

export function colorForSection(index: number): { hex: string; cesium: Color } {
	const hex = SECTION_COLORS[index % SECTION_COLORS.length];
	return { hex, cesium: Color.fromCssColorString(hex) };
}

// ── Section circuits (level 1) ──────────────────────────────────────────────

export type EnrichedSection = {
	key: string;
	section: ISectionCircuitInfo;
	color: { hex: string; cesium: Color };
	lineIds: number[];
	/** All spanLineset ids in this section — sent to `/calculate`. */
	spanLinesetIds: number[];
};

/** Flattens spanLinesets/lines into the shape the panel renders + sends. */
export function enrichSections(sectionInfo: ISectionInfo | null | undefined): EnrichedSection[] {
	const circuits = sectionInfo?.sectionCircuits;
	if (!circuits || circuits.length === 0) return [];
	return circuits.map((s, i) => {
		const lineIds: number[] = [];
		const spanLinesetIds: number[] = [];
		for (const span of s.spanLinesets ?? []) {
			for (const line of span.lines ?? []) lineIds.push(line.id);
			spanLinesetIds.push(span.id);
		}
		return {
			key: `${i}-${s.firstMastId}-${s.lastMastId}-${s.conductorType}`,
			section: s,
			color: colorForSection(i),
			lineIds,
			spanLinesetIds
		};
	});
}

// ── Mast grouping (Master tab) ──────────────────────────────────────────────

export type MastGroup = {
	key: string;
	label: string;
	masts: ISectionInfoMast[];
};

/** Groups masts by `mastFunction`; the display label uses `mastFunctionName`. */
export function groupMastsByFunction(masts: ISectionInfoMast[] | undefined): MastGroup[] {
	if (!masts?.length) return [];
	const groups = new Map<string, MastGroup>();
	for (const mast of masts) {
		const key = mast.mastFunction || 'UNKNOWN';
		let group = groups.get(key);
		if (!group) {
			group = { key, label: mast.mastFunctionName || mast.mastFunction || '—', masts: [] };
			groups.set(key, group);
		}
		group.masts.push(mast);
	}
	return Array.from(groups.values());
}

// ── Mast function abbreviation (map labels) ─────────────────────────────────

/**
 * Map of full Norwegian mast-function names → 1–2 letter codes used on the
 * map label. Lookups are case-insensitive (keys are lowercase).
 * "Ikke kalkulert" collapses to an en-dash to signal a non-value.
 */
const MAST_FUNCTION_ABBREVIATIONS: Record<string, string> = {
	bæremast: 'B',
	vinkelbæremast: 'VB',
	endemast: 'E',
	forankringsmast: 'F',
	vinkelforankringsmast: 'VF',
	avgreningsmast: 'A',
	avgrening: 'A',
	'ikke kalkulert': '–'
};

export function abbreviateMastFunction(name: string | undefined | null): string {
	if (!name) return '';
	const trimmed = name.trim();
	if (!trimmed) return '';

	const direct = MAST_FUNCTION_ABBREVIATIONS[trimmed.toLowerCase()];
	if (direct) return direct;

	// Already a short code (e.g. "B", "VB", "F+VF") — pass through unchanged.
	if (/^[A-ZÆØÅ+]{1,5}$/.test(trimmed)) return trimmed;

	// Compound name like "Forankringsmast + Vinkelforankringsmast" — abbreviate each side.
	if (trimmed.includes('+')) {
		return trimmed
			.split('+')
			.map((part) => abbreviateMastFunction(part.trim()))
			.filter(Boolean)
			.join('+');
	}

	// Unknown name — fall back to the original so the user can spot it.
	return trimmed;
}

// ── Montage stretch constants (Ledninger tab) ───────────────────────────────

export const MONTAGE_STRETCH_MIN = 0;
export const MONTAGE_STRETCH_MAX = 120;
export const MONTAGE_STRETCH_DEFAULT = 0;

// ── Misc constants ──────────────────────────────────────────────────────────

export const MAST_GROUP_DEFAULT_LIMIT = 7;
export const POLYGON_SECTION_CALCULATION_IDB_KEY = 'polygonSectionCalculation';
